import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillTransfer, faClock, faThumbsUp, faCheckCircle, faChartBar,
  faTimes, faCheck, faBan, faUpload, faRotate, faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { fmtMoney } from '../../utils/formatMoney';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';

const WITHDRAWAL_STATUS_MAP: Record<string, StatusDef> = {
  pending:   { bg: 'bg-amber-100 dark:bg-amber-900/30',  text: 'text-amber-700 dark:text-amber-400',  icon: faClock,        label: 'รออนุมัติ'  },
  approved:  { bg: 'bg-blue-100 dark:bg-blue-900/30',    text: 'text-blue-700 dark:text-blue-400',    icon: faThumbsUp,     label: 'อนุมัติแล้ว' },
  completed: { bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',  icon: faCheckCircle,  label: 'เสร็จสิ้น'   },
  rejected:  { bg: 'bg-red-100 dark:bg-red-900/30',      text: 'text-red-600 dark:text-red-400',      icon: faTimes,        label: 'ปฏิเสธ'      },
  cancelled: { bg: 'bg-gray-100 dark:bg-slate-700',      text: 'text-gray-600 dark:text-slate-400',   icon: faBan,          label: 'ยกเลิก'      },
};

type StatusFilter = 'all' | 'pending' | 'approved' | 'completed' | 'rejected' | 'cancelled';

interface Withdrawal {
  id: string;
  withdrawal_number: string;
  amount: number;
  status: string;
  bank_account_name: string;
  bank_account_number: string;
  bank_name: string;
  currency: string;
  wallet_address?: string;
  network?: string;
  usdt_rate?: number;
  fee_amount?: number;
  fee_percent?: number;
  usdt_amount?: number;
  slip_url?: string;
  note?: string;
  rejected_reason?: string;
  approved_by_name?: string;
  approved_at?: string;
  completed_at?: string;
  created_at: string;
  reseller: { id: string; name: string; email: string };
}

interface WithdrawalStats {
  pending: number;
  pending_amount: number;
  approved: number;
  approved_amount: number;
  completed: number;
  completed_amount: number;
  total: number;
  total_amount: number;
}

const STATUS_TABS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'pending', label: 'รออนุมัติ' },
  { key: 'approved', label: 'อนุมัติแล้ว' },
  { key: 'completed', label: 'เสร็จสิ้น' },
  { key: 'rejected', label: 'ปฏิเสธ' },
  { key: 'cancelled', label: 'ยกเลิก' },
];

const ResellerWithdrawalsPage: React.FC = () => {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [stats, setStats] = useState<WithdrawalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedWd, setSelectedWd] = useState<Withdrawal | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'complete' | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [slipUrl, setSlipUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [wdRes, statsRes] = await Promise.all([
        merchantApi.get(`/admin/resellers/withdrawals${params}`),
        merchantApi.get('/admin/resellers/withdrawals/stats'),
      ]);
      setWithdrawals(wdRes.data.data || []);
      setStats(statsRes.data.data);
    } catch {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const socket = (window as any).__adminSocket;
    if (socket) {
      socket.on('reseller-withdrawal-created', () => loadData());
      socket.on('reseller-withdrawal-updated', () => loadData());
      return () => {
        socket.off('reseller-withdrawal-created');
        socket.off('reseller-withdrawal-updated');
      };
    }
  }, [loadData]);

  const handleApprove = async () => {
    if (!selectedWd) return;
    setActionLoading(true);
    try {
      await merchantApi.post(`/admin/resellers/withdrawals/${selectedWd.id}/approve`);
      toast.success('อนุมัติสำเร็จ');
      closeModal();
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedWd || !rejectReason.trim()) {
      toast.error('กรุณาระบุเหตุผล');
      return;
    }
    setActionLoading(true);
    try {
      await merchantApi.post(`/admin/resellers/withdrawals/${selectedWd.id}/reject`, { rejected_reason: rejectReason });
      toast.success('ปฏิเสธและคืนเงินสำเร็จ');
      closeModal();
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('ไฟล์ใหญ่เกิน 5MB'); return; }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const clearFile = () => {
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleComplete = async () => {
    if (!selectedWd) return;
    if (!selectedFile && !slipUrl.trim()) {
      toast.error('กรุณาอัพโหลดสลิป หรือใส่ URL');
      return;
    }
    setActionLoading(true);
    try {
      if (selectedFile) {
        const formData = new FormData();
        formData.append('slip', selectedFile);
        await merchantApi.post(
          `/admin/resellers/withdrawals/${selectedWd.id}/complete-with-upload`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded * 100) / (p.total || 1))),
          },
        );
      } else {
        await merchantApi.post(`/admin/resellers/withdrawals/${selectedWd.id}/complete`, { slip_url: slipUrl });
      }
      toast.success('เสร็จสิ้น');
      closeModal();
      clearFile();
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setActionLoading(false);
      setUploadProgress(0);
    }
  };

  const closeModal = () => {
    if (actionLoading) return;
    setSelectedWd(null);
    setActionType(null);
    setRejectReason('');
    setSlipUrl('');
    clearFile();
  };

  const openAction = (wd: Withdrawal, type: 'approve' | 'reject' | 'complete') => {
    setSelectedWd(wd);
    setActionType(type);
    setRejectReason('');
    setSlipUrl('');
    clearFile();
  };

  const getTabCount = (key: StatusFilter): number | undefined => {
    if (!stats) return undefined;
    if (key === 'all') return stats.total;
    if (key === 'pending') return stats.pending;
    if (key === 'approved') return stats.approved;
    if (key === 'completed') return stats.completed;
    return undefined;
  };

  const statCards: StatCardDef[] = useMemo(() => {
    if (!stats) return [];
    return [
      {
        icon: faClock,
        iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        label: 'รออนุมัติ',
        value: <span className="text-amber-600 dark:text-amber-400">{stats.pending.toLocaleString()}</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">฿{fmtMoney(stats.pending_amount)}</span>,
      },
      {
        icon: faThumbsUp,
        iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
        label: 'อนุมัติแล้ว',
        value: <span className="text-blue-600 dark:text-blue-400">{stats.approved.toLocaleString()}</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">฿{fmtMoney(stats.approved_amount)}</span>,
      },
      {
        icon: faCheckCircle,
        iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        label: 'เสร็จสิ้น',
        value: <span className="text-emerald-600 dark:text-emerald-400">{stats.completed.toLocaleString()}</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">฿{fmtMoney(stats.completed_amount)}</span>,
      },
      {
        icon: faChartBar,
        iconClassName: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
        label: 'ทั้งหมด',
        value: <span className="text-violet-600 dark:text-violet-400">{stats.total.toLocaleString()}</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">฿{fmtMoney(stats.total_amount)}</span>,
      },
    ];
  }, [stats]);

  const columns: Column<Withdrawal>[] = useMemo(() => [
    {
      key: 'withdrawal_number',
      header: 'เลขที่',
      sortable: true,
      render: (wd) => (
        <strong className="text-[13px] text-gray-800 dark:text-slate-100">{wd.withdrawal_number}</strong>
      ),
    },
    {
      key: 'reseller',
      header: 'Reseller',
      sortable: true,
      render: (wd) => (
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-gray-800 dark:text-slate-100 truncate">{wd.reseller?.name}</p>
          <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate">{wd.reseller?.email}</p>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'จำนวนเงิน',
      sortable: true,
      render: (wd) => (
        <div>
          <strong className="text-[15px] text-emerald-600 dark:text-emerald-400">฿{fmtMoney(wd.amount)}</strong>
          {wd.currency === 'USDT' && wd.usdt_amount && (
            <div className="mt-1 text-[11px] space-y-0.5">
              <div className="text-red-500 dark:text-red-400">Fee {wd.fee_percent ?? 5}%: -฿{fmtMoney(wd.fee_amount || 0)}</div>
              <div className="text-gray-500 dark:text-slate-400">Rate: 1 USDT = ฿{wd.usdt_rate?.toLocaleString()}</div>
              <div className="font-bold text-amber-600 dark:text-amber-400">
                {wd.usdt_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} USDT
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'bank',
      header: 'ปลายทาง',
      render: (wd) => (
        <div className="text-sm min-w-0">
          {wd.currency === 'USDT' ? (
            <>
              <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-xs font-medium">USDT</span>
              <span className="text-xs text-gray-400 dark:text-slate-500 ml-1">{wd.network}</span>
              <p className="text-xs text-gray-400 dark:text-slate-500 font-mono mt-0.5 truncate max-w-[180px]">{wd.wallet_address}</p>
            </>
          ) : (
            <>
              <p className="text-gray-700 dark:text-slate-200">{wd.bank_name}</p>
              <p className="text-xs text-gray-400 dark:text-slate-500">{wd.bank_account_number} - {wd.bank_account_name}</p>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'สถานะ',
      sortable: true,
      render: (wd) => (
        <div>
          <StatusBadge status={wd.status} statusMap={WITHDRAWAL_STATUS_MAP} />
          {wd.rejected_reason && (
            <p className="text-[11px] text-red-500 dark:text-red-400 mt-1">{wd.rejected_reason}</p>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'วันที่',
      sortable: true,
      render: (wd) => (
        <span className="text-[12px] text-gray-400 dark:text-slate-500">
          {new Date(wd.created_at).toLocaleString('th-TH')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'จัดการ',
      width: '180px',
      render: (wd) => (
        <div className="flex items-center gap-1 flex-wrap">
          {wd.status === 'pending' && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openAction(wd, 'approve'); }}
                className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
              >
                อนุมัติ
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openAction(wd, 'reject'); }}
                className="px-2 py-1 rounded-md text-[11px] font-medium bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
              >
                ปฏิเสธ
              </button>
            </>
          )}
          {wd.status === 'approved' && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openAction(wd, 'complete'); }}
                className="px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
              >
                เสร็จสิ้น
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openAction(wd, 'reject'); }}
                className="px-2 py-1 rounded-md text-[11px] font-medium bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
              >
                ปฏิเสธ
              </button>
            </>
          )}
          {wd.slip_url && (
            <a
              href={wd.slip_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors inline-flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px]" />
              สลิป
            </a>
          )}
        </div>
      ),
    },
  ], []);

  const handleFilterReset = () => setStatusFilter('all');

  const modalTitle = actionType === 'approve' ? 'อนุมัติถอนเงิน'
    : actionType === 'reject' ? 'ปฏิเสธถอนเงิน'
    : 'เสร็จสิ้น (แนบสลิป)';

  const modalIcon = actionType === 'approve' ? faCheck
    : actionType === 'reject' ? faBan
    : faUpload;

  const modalIconColor = actionType === 'approve' ? 'text-blue-500'
    : actionType === 'reject' ? 'text-red-500'
    : 'text-emerald-500';

  const wdSummary = selectedWd && (
    <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm">
      <div className="flex justify-between gap-4">
        <span className="text-gray-500 dark:text-slate-400">เลขที่:</span>
        <span className="font-medium text-gray-800 dark:text-slate-100">{selectedWd.withdrawal_number}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-500 dark:text-slate-400">Reseller:</span>
        <span className="text-gray-800 dark:text-slate-100">{selectedWd.reseller?.name}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-500 dark:text-slate-400">จำนวนเงิน:</span>
        <strong className="text-lg text-emerald-600 dark:text-emerald-400">฿{fmtMoney(selectedWd.amount)}</strong>
      </div>
      {selectedWd.currency === 'USDT' ? (
        <>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500 dark:text-slate-400">สกุลเงิน:</span>
            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-xs font-medium">
              USDT ({selectedWd.network})
            </span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500 dark:text-slate-400">Wallet:</span>
            <span className="font-mono text-xs max-w-[200px] truncate text-gray-700 dark:text-slate-200">{selectedWd.wallet_address}</span>
          </div>
          {selectedWd.usdt_rate && (
            <>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500 dark:text-slate-400">Rate:</span>
                <span className="text-gray-700 dark:text-slate-200">1 USDT = ฿{selectedWd.usdt_rate}</span>
              </div>
              <div className="flex justify-between gap-4 text-red-500 dark:text-red-400">
                <span>Fee ({selectedWd.fee_percent ?? 5}%):</span>
                <span>-฿{selectedWd.fee_amount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between gap-4 font-bold text-amber-700 dark:text-amber-400">
                <span>USDT Amount:</span>
                <span>{selectedWd.usdt_amount?.toLocaleString()} USDT</span>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500 dark:text-slate-400">บัญชี:</span>
            <span className="text-gray-700 dark:text-slate-200">{selectedWd.bank_name} - {selectedWd.bank_account_number}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500 dark:text-slate-400">ชื่อบัญชี:</span>
            <span className="text-gray-700 dark:text-slate-200">{selectedWd.bank_account_name}</span>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faMoneyBillTransfer}
        title="Reseller Withdrawals"
        subtitle={`จัดการคำขอถอนเงิน Commission ของ Reseller • ${withdrawals.length.toLocaleString()} รายการ`}
      />

      <UiFilterPanel
        onSearch={() => {}}
        onReset={handleFilterReset}
        loading={loading}
        actions={
          <button
            type="button"
            onClick={() => loadData()}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      >
        <div className="w-fit shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <div className="inline-flex items-stretch h-10 gap-0.5 border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5 w-fit flex-wrap">
            {STATUS_TABS.map((tab) => {
              const count = getTabCount(tab.key);
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setStatusFilter(tab.key)}
                  className={`inline-flex items-center gap-1.5 px-3 h-full rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200
                    ${statusFilter === tab.key
                      ? 'bg-white dark:bg-slate-600 text-gray-800 dark:text-slate-100 shadow-sm'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
                    }`}
                >
                  {tab.label}
                  {count !== undefined && (
                    <span className={`px-1.5 min-w-[1.25rem] text-center rounded-full text-xs font-bold leading-none py-0.5
                      ${statusFilter === tab.key
                        ? 'bg-gray-100 dark:bg-slate-500 text-gray-600 dark:text-slate-200'
                        : 'bg-gray-200/60 dark:bg-slate-600/60 text-gray-400 dark:text-slate-500'
                      }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </UiFilterPanel>

      <UiStatsGrid cards={statCards} loading={loading && !stats} cols={4} />

      <DataTable
        data={withdrawals}
        columns={columns}
        loading={loading}
        keyExtractor={(wd) => wd.id}
        emptyMessage="ไม่มีรายการถอนเงิน"
        emptyIcon={<FontAwesomeIcon icon={faMoneyBillTransfer} className="text-5xl text-gray-200 dark:text-slate-600" />}
        rowClassName={(wd) => wd.status === 'pending' ? 'bg-amber-50/30 dark:bg-amber-900/10' : ''}
        hoverable
        compact
        pagination={{
          enabled: true,
          pageSize: 20,
          pageSizeOptions: [10, 20, 50],
          showPageSizeSelector: true,
          showInfo: true,
          showFirstLast: true,
        }}
      />

      <UiModal
        open={!!selectedWd && !!actionType}
        onClose={closeModal}
        title={modalTitle}
        icon={modalIcon}
        iconColor={modalIconColor}
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton
              variant="secondary"
              onClick={closeModal}
              disabled={actionLoading}
              className="flex-1 justify-center"
            >
              ยกเลิก
            </UiButton>
            {actionType === 'approve' && (
              <UiButton
                loading={actionLoading}
                onClick={handleApprove}
                className="flex-1 justify-center"
              >
                อนุมัติ
              </UiButton>
            )}
            {actionType === 'reject' && (
              <UiButton
                variant="danger"
                loading={actionLoading}
                disabled={!rejectReason.trim()}
                onClick={handleReject}
                className="flex-1 justify-center"
              >
                ปฏิเสธ & คืนเงิน
              </UiButton>
            )}
            {actionType === 'complete' && (
              <UiButton
                loading={actionLoading}
                disabled={!selectedFile && !slipUrl.trim()}
                onClick={handleComplete}
                className="flex-1 justify-center !bg-emerald-600 hover:!bg-emerald-700"
              >
                เสร็จสิ้น
              </UiButton>
            )}
          </div>
        }
      >
        {selectedWd && (
          <div className="space-y-4">
            {wdSummary}

            {actionType === 'reject' && (
              <div>
                <label className={LABEL_CLASS}>เหตุผลในการปฏิเสธ *</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  required
                  className={INPUT_CLASS}
                  placeholder="ระบุเหตุผล..."
                />
              </div>
            )}

            {actionType === 'complete' && (
              <div className="space-y-3">
                <div>
                  <label className={LABEL_CLASS}>อัพโหลดสลิป *</label>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
                      ${previewUrl
                        ? 'border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-300 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10'
                      }`}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {previewUrl ? (
                      <div className="space-y-2">
                        <img src={previewUrl} alt="Preview" className="max-h-40 rounded-lg mx-auto shadow-md" />
                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">{selectedFile?.name}</p>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); clearFile(); }}
                          className="text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                        >
                          ลบไฟล์
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <FontAwesomeIcon icon={faUpload} className="text-2xl text-gray-400 dark:text-slate-500" />
                        <p className="text-gray-500 dark:text-slate-400 text-sm">คลิกเพื่อเลือกไฟล์สลิป</p>
                        <p className="text-xs text-gray-400 dark:text-slate-500">JPEG, PNG, WebP, GIF (ไม่เกิน 5MB)</p>
                      </div>
                    )}
                  </div>
                  {actionLoading && uploadProgress > 0 && (
                    <div className="mt-2">
                      <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mb-1">
                        <span>กำลังอัพโหลด...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-2">
                        <div
                          className="bg-emerald-500 h-2 rounded-full transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {!selectedFile && (
                  <div>
                    <label className={LABEL_CLASS}>หรือใส่ URL สลิป</label>
                    <input
                      type="url"
                      value={slipUrl}
                      onChange={(e) => setSlipUrl(e.target.value)}
                      className={INPUT_CLASS}
                      placeholder="https://..."
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </UiModal>
    </div>
  );
};

export default ResellerWithdrawalsPage;
